import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Card,
  Typography,
  Input,
  Select,
  Empty,
  Col,
  Row,
  Spin,
  Tooltip,
  Table,
  Tabs,
  Segmented,
  Modal,
  message,
  Tag,
} from "antd";

import { postReq } from "../api";
import { toastNotification } from "../utils/toasthelper";
import JobCardComponent from "../components/jobPostingCard";
import moment from "moment";

const { Title, Paragraph } = Typography;
const { Option } = Select;
const formatFullAddress = (location = {}) => {
  if (!location) return { location: "-", requirementType: "Remote" };

  const { locality, city, state, country, displayText } = location;

  const parts = [
    locality,
    city,
    state,
    country === "IN" ? "India" : country,
  ].filter(Boolean);

  // Fallback if everything is empty
  if ((!parts.length || parts.length == 1) && displayText) {
    return { location: displayText, requirementType: "Remote" };
  }

  return { location: parts.join(", "), requirementType: "Onsite" };
};

const RelevantJobs = () => {
  const RELEVANT_OPTIONS = [
    { label: "Pending", value: "pending" },
    { label: "ICP", value: "ICP" },
    { label: "Non_ICP", value: "Non_ICP" },
  ];
  const [open, setOpen] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const [value, setValue] = useState("pending");
  const [saving, setSaving] = useState(false);

  const openRelevantModal = (record) => {
    setActiveRecord(record);
    setValue(record?.icp || "pending");
    setOpen(true);
  };

  const updateStatusApi = async ({ id, relevant }) => {
    const res = await postReq("common/update_icpStatus", {
      id,
      icp: relevant,
    });
    if (res.status == 1) {
      return true;
    } else {
      message.error(res.message || "Error");
      return false;
    }
  };

  const handleChange = (next) => {
    if (!activeRecord) return;

    const prev = activeRecord.icp || "pending";
    if (next === prev) return;

    // update UI selection (optional) before confirm
    setValue(next);

    Modal.confirm({
      title: "Confirm change",
      content: `Change status from "${prev}" to "${next}"?`,
      okText: "Yes, Update",
      cancelText: "Cancel",
      onCancel: () => setValue(prev), // revert UI
      onOk: async () => {
        try {
          setSaving(true);

          const res = await updateStatusApi({
            id: activeRecord._id,
            relevant: next,
          });
          if (res) {
            setData((prevData) =>
              prevData.map((row) =>
                row._id === activeRecord._id ? { ...row, icp: next } : row,
              ),
            );
            setActiveRecord((r) => ({ ...r, icp: next }));
            message.success("Status updated");
            setOpen(false);
          }
        } catch (err) {
          console.log("/**/*/*/*", err);
          message.error(err?.response?.data?.message || "Update failed");
          setValue(prev); // revert if failed
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const columns = [
    {
      title: "ICP",
      dataIndex: "icp",
      key: "icp",
      render: (_, record) => {
        const v = record.icp || "pending";
        const vBy = record.icpBy || { name: "" };

        const color =
          v === "ICP" ? "green" : v === "Non_ICP" ? "red" : "default";

        return (
          <Tag
            color={color}
            style={{
              cursor: "pointer",
              userSelect: "none",
              borderRadius: "10px",
              fontWeight: 700,
            }}
            onClick={() => openRelevantModal(record)}
          >
            {v == "pending" ? v : vBy.name}
          </Tag>
        );
      },
    },
    {
      title: "Relevant",
      dataIndex: "relevant",
      key: "relevant",
      render: (_, record) => {
        const v = record.relevant || "pending";
        const vBy = record.relevantBy || { name: "" };
        const color =
          v === "relevant" ? "green" : v === "non_relevant" ? "red" : "default";

        return (
          <Tag
            color={color}
            style={{
              cursor: "pointer",
              userSelect: "none",
              borderRadius: "10px",
              fontWeight: 700,
            }}
          >
            {vBy.name}
          </Tag>
        );
      },
    },
    {
      title: "View",
      dataIndex: "source",
      key: "name",
      render: (_, record) => {
        return (
          <a href={record?.source?.url || "#"} target="_blank" rel="noreferrer">
            {/* {record?.source?.name || "-"} */}
            {record.jobId}
          </a>
        );
      },
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "name",
      render: (_, record) => {
        const text = record?.title || "-";
        return (
          <Tooltip title={text} placement="topLeft">
            <Paragraph
              style={{ margin: 0, maxWidth: 170 }}
              ellipsis={{ rows: 2 }}
            >
              {text}
            </Paragraph>
          </Tooltip>
        );
      },
    },
    {
      title: "Contract Type",
      dataIndex: "employement",
      key: "contractType",
      render: (_, record) => `${record?.employment?.contractType || "-"}`,
    },
    {
      title: "Contract Time",
      dataIndex: "employement",
      key: "contractTime",
      render: (_, record) => `${record?.employment?.contractTime || "-"}`,
    },
    {
      title: "Remote Type",
      dataIndex: "employement",
      key: "contractTime",
      render: (_, record) =>
        `${formatFullAddress(record.location).requirementType || "-"}`,
    },
    {
      title: "Company Name",
      dataIndex: "company",
      key: "company",
      render: (_, record) => `${record?.company?.name}`,
    },
    {
      title: "Salary Min/Max",
      dataIndex: "salary",
      key: "salary",
      render: (_, record) => {
        return `${record.salary.min || "-"}/${record.salary.max || "-"}`;
      },
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (_, record) => {
        const location = formatFullAddress(record.location).location;
        return `${location}`;
      },
    },
    {
      title: "Posted At",
      dataIndex: "posted",
      key: "posted",
      render: (_, record) => {
        return `${moment(record.postedAt).format("DD/MM/YYYY HH:mm")}`;
      },
    },
    {
      title: "Description",
      key: "description",
      render: (_, record) => {
        const text = record?.description?.text || "-";
        return (
          <Tooltip title={text} placement="topLeft">
            <Paragraph
              style={{ margin: 0, maxWidth: 450 }}
              ellipsis={{ rows: 2 }}
            >
              {text}
            </Paragraph>
          </Tooltip>
        );
      },
    },
  ];
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [view, setView] = useState("table");

  const [tableParams, setTableParams] = useState({
    page: 1,
    limit: 20,
    search: "",
  });

  const [filter, setFilter] = useState({ contractType: "", contractTime: "" });

  const hasMore = data.length < total;

  const mergeUniqueById = (prev, next) => {
    const map = new Map(prev.map((x) => [x._id, x]));
    next.forEach((x) => map.set(x._id, x));
    return Array.from(map.values());
  };

  const loadingRef = useRef(false);

  const fetchData = useCallback(async (params, filterObj) => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);

    try {
      const results = await postReq("common/list_jobposting", {
        ...params,
        filter: filterObj,
        relevant: true,
      });

      if (results.status === 1) {
        setTotal(results.total || 0);

        if (params.page === 1) {
          setData(results.data || []);
        } else {
          setData((prev) => mergeUniqueById(prev, results.data || []));
        }
      } else {
        setData([]);
        setTotal(0);
      }
    } catch (error) {
      toastNotification("error", error.message);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []); // ✅ stable (no loading/hasMore/tableParams deps)

  useEffect(() => {
    fetchData(tableParams, filter);
  }, [
    tableParams.page,
    tableParams.limit,
    filter.contractType,
    filter.contractTime,
    fetchData,
  ]);
  useEffect(() => {
    const t = setTimeout(() => {
      fetchData(tableParams, filter);
    }, 300);

    return () => clearTimeout(t);
  }, [tableParams.search]);
  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams((prev) => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
    }));

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  // ✅ Reset pagination when filter/search changes
  useEffect(() => {
    setData([]);
    setTotal(0);
    setTableParams((p) => ({ ...p, page: 1 }));
  }, [filter, tableParams.search]);

  // ✅ Card body scroll container ref
  const scrollRef = useRef(null);

  // ✅ Infinite scroll sentinel ref
  const loadMoreRef = useRef(null);

  useEffect(() => {
    const rootEl = scrollRef.current;
    const targetEl = loadMoreRef.current;

    if (!rootEl || !targetEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (loading) return;
        if (!hasMore) return;

        setTableParams((p) => ({ ...p, page: p.page + 1 }));
      },
      {
        root: rootEl, // ✅ IMPORTANT: observe inside card scroll
        rootMargin: "200px",
        threshold: 0,
      },
    );

    observer.observe(targetEl);
    return () => observer.disconnect();
  }, [loading, hasMore]);

  const handleOnChange = (e) => {
    const value = e.target.value;
    setTableParams((p) => ({ ...p, search: value, page: 1 }));
  };

  const tabItems = useMemo(
    () => [
      {
        key: "table",
        label: "Table View",
        children: (
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: tableParams.page,
              pageSize: tableParams.limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 30],
              itemRender: (current, type, originalElement) => {
                if (type === "prev") return <a>Prev</a>;
                if (type === "next") return <a>Next</a>;
                return originalElement;
              },
              showTotal: (total, range) => (
                <span style={{ color: "#6b7280" }}>
                  Showing {range[0]}–{range[1]} of {total}
                </span>
              ),
            }}
            loading={loading}
            scroll={{ x: "max-content" }}
            onChange={handleTableChange}
          />
        ),
      },
      {
        key: "cards",
        label: "Card View",
        children: (
          <div
            ref={scrollRef}
            style={{
              height: "70vh",
              overflowY: "auto",
              overflowX: "hidden",
              paddingRight: 0,
            }}
          >
            {data.length > 0 ? (
              <>
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  {data.map((job) => (
                    <Col
                      key={job._id}
                      xs={24}
                      sm={12}
                      md={8}
                      lg={8}
                      style={{ marginBottom: 20 }}
                    >
                      <JobCardComponent
                        job={job}
                        onClick={openRelevantModal}
                        relevant={true}
                      />
                    </Col>
                  ))}
                </Row>

                {/* Sentinel inside scroll container */}
                <div ref={loadMoreRef} style={{ height: 1 }} />

                <div style={{ textAlign: "center", padding: "12px 0" }}>
                  {loading && <Spin />}
                  {!loading && !hasMore && <div>No more jobs.</div>}
                </div>
              </>
            ) : (
              <div style={{ padding: 24, textAlign: "center" }}>
                {loading ? <Spin /> : <Empty description="No Data Available" />}
                <div ref={loadMoreRef} style={{ height: 1 }} />
              </div>
            )}
          </div>
        ),
      },
    ],
    [
      columns,
      data,
      total,
      loading,
      tableParams.page,
      tableParams.limit,
      handleTableChange,
      JobCardComponent,
      hasMore,
      loadMoreRef,
    ],
  );
  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px",
            borderRadius: "20px 20px 0px 0px",
            margin: "0px -24px",
          }}
        >
          <div>
            <Title
              level={4}
              style={{
                marginBottom: 0,
                fontSize: 20,
                fontWeight: 800,
                color: "#13487D",
              }}
            >
              Job Posting
            </Title>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Select
              placeholder="Contract Type"
              style={{ width: 150 }}
              defaultValue={""}
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, contractType: value }))
              }
            >
              {[
                { _id: "", value: "Select" },
                { _id: "permanent", value: "permanent" },
                { _id: "contract", value: "contract" },
              ].map((d, i) => (
                <Option key={i} value={d._id}>
                  {d.value}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="Contract Time"
              style={{ width: 150 }}
              defaultValue={""}
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, contractTime: value }))
              }
            >
              {[
                { _id: "", value: "Select" },
                { _id: "full_time", value: "Full Time" },
                { _id: "part_time", value: "Part Time" },
              ].map((d, i) => (
                <Option key={i} value={d._id}>
                  {d.value}
                </Option>
              ))}
            </Select>
            {/* <Select
              placeholder="Contract Time"
              style={{ width: 150 }}
              defaultValue={""}
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, remoteType: value }))
              }
            >
              {[
                { _id: "", value: "Select" },
                { _id: "remote", value: "Remote" },
                { _id: "onsite", value: "Onsite" },
              ].map((d, i) => (
                <Option key={i} value={d._id}>
                  {d.value}
                </Option>
              ))}
            </Select> */}

            <div style={{ width: 300 }}>
              <Input
                placeholder="Search By Any Field"
                bordered={false}
                onChange={handleOnChange}
              />
            </div>
          </div>
        </div>
      }
      style={{
        paddingLeft: "16px",
        color: "#13487D",
        borderRadius: "18px",
        maxWidth: "100%",
        overflow: "hidden",
      }}
      bordered={false}
    >
      <Tabs
        activeKey={view}
        onChange={(key) => {
          setView(key);

          // reset only pagination when switching view
          setTableParams((prev) => ({
            ...prev,
            page: 1,
            limit: key === "table" ? 10 : 12, // choose your defaults
          }));
        }}
        items={tabItems}
        destroyInactiveTabPane
        // ✅ destroyInactiveTabPane is important so your infinite-scroll sentinel
        // doesn't keep triggering while the tab is hidden
      />
      <Modal
        title="Set ICP"
        open={open}
        onCancel={() => !saving && setOpen(false)}
        footer={null}
        destroyOnClose
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ color: "#6b7280" }}>
            Job: <b>{activeRecord?.title || activeRecord?._id}</b>
          </div>

          <Segmented
            block
            value={value}
            options={RELEVANT_OPTIONS}
            onChange={handleChange}
            disabled={saving}
          />

          <div style={{ color: "#6b7280", fontSize: 12 }}>
            Changing selection will ask for confirmation.
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default RelevantJobs;
